import { useCallback } from 'react'
import { WbsItemSearchConditionV2VO } from '../../../../domain/value-object/WbsItemSearchConditionV2Vo'
import store from '../../../../store'
import { addScreenMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'
import { useWbsItemRepository } from '../../../../services/adaptors/wbsItemRepositoryAdaptor'
import { useRootWbsItems } from './useRootWbsItems'
import {
  buildExcludeResult,
  ExcludeResult,
  selectExcludedMessageId,
  selectNotFoundUuids,
  selectUnconfirmedUuids,
  toUnchangedResult,
} from './excludeRootUuids'

// 削除されたWBSが rootUuids に残っているとバックエンドが NotFoundException を投げて
// 検索が継続的に失敗するため、検索条件適用時に現在のプロジェクトに無い rootUuid を除外する。
// ただし「テナント全体に公開」の保存条件は他プロジェクトからも呼び出されるため、
// 他プロジェクトのWBSは検索からだけ外し、保存し直す対象は削除済みのWBSに限る。
export const useExcludeInaccessibleRootUuids = (
  functionUuid: string,
  projectUuid: string
) => {
  const getRootWbsItems = useRootWbsItems(projectUuid, false)
  const { fetchAsReferencedEntityByUuid, fetchAsReferencedEntity } =
    useWbsItemRepository()

  // 成果物を含まない軽い一覧で確認し、そこに無いもの（成果物単体である可能性が
  // ある分）だけを個別に確認する。常時成果物込みの重い一覧を取得すると、
  // 成果物モードを使わないユーザーにも毎回コストがかかってしまうため。
  // 個別確認に失敗した場合は判定できないため undefined を返す。
  const findUuidsOutsideProject = useCallback(
    async (rootUuids: string[]): Promise<string[] | undefined> => {
      const validItems = await getRootWbsItems()
      const validUuids = new Set(validItems.map(item => item.uuid))
      const missingUuids = rootUuids.filter(uuid => !validUuids.has(uuid))
      if (missingUuids.length === 0) return []

      const results = await Promise.allSettled(
        missingUuids.map(uuid =>
          fetchAsReferencedEntityByUuid(projectUuid, uuid)
        )
      )
      return selectUnconfirmedUuids(missingUuids, results)
    },
    [getRootWbsItems, fetchAsReferencedEntityByUuid, projectUuid]
  )

  const findDeletedUuids = useCallback(
    async (uuids: string[]): Promise<string[]> => {
      const results = await Promise.allSettled(
        uuids.map(uuid => fetchAsReferencedEntity(uuid))
      )
      return selectNotFoundUuids(uuids, results)
    },
    [fetchAsReferencedEntity]
  )

  const exclude = useCallback(
    async (condition: WbsItemSearchConditionV2VO): Promise<ExcludeResult> => {
      if (!condition.rootUuids || condition.rootUuids.length === 0) {
        return toUnchangedResult(condition)
      }
      try {
        const outsideUuids = await findUuidsOutsideProject(condition.rootUuids)
        if (!outsideUuids || outsideUuids.length === 0) {
          return toUnchangedResult(condition)
        }
        const deletedUuids = await findDeletedUuids(outsideUuids)
        return buildExcludeResult(condition, outsideUuids, deletedUuids)
      } catch {
        // 検証用APIが失敗した場合は元の条件で続行する（フェイルオープン）。
        // 削除済みUUIDが残っていれば検索時に従来通りのエラーが出るが、
        // 検証API障害で画面そのものを使えなくするほうが弊害が大きい。
        return toUnchangedResult(condition)
      }
    },
    [findUuidsOutsideProject, findDeletedUuids]
  )

  const notify = useCallback(
    (result: ExcludeResult) => {
      const messageId = selectExcludedMessageId(result)
      if (!messageId) return
      store.dispatch(
        addScreenMessage(functionUuid, {
          type: MessageLevel.WARN,
          title: intl.formatMessage({ id: messageId }),
        })
      )
    },
    [functionUuid]
  )

  return { exclude, notify }
}
